import React, { useEffect, useRef, useState } from 'react';
import { agentApi } from '../../../services/agentApi';

/**
 * Downloads an agent file. With a file id, every click asks `agentApi.fileLink(file_id)` for a
 * fresh signed link (stored URLs expire after 24 h); the stored `url` is used only when there is
 * no file id.
 */
const DownloadButton: React.FC<{ fileId?: number | null; url?: string | null; children: React.ReactNode }> = ({
  fileId, url, children,
}) => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  if (fileId === null || fileId === undefined) {
    if (!url) return null;
    return <a className="agent-download" href={url} target="_blank" rel="noopener noreferrer">{children}</a>;
  }

  const open = async () => {
    setError('');
    setLoading(true);
    // Open the tab within the click so pop-up blockers allow it, then point it at the link.
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    try {
      const link = await agentApi.fileLink(fileId);
      if (tab) tab.location.href = link.url;
      else window.location.assign(link.url);
    } catch (err: any) {
      tab?.close();
      if (mounted.current) setError(err?.message || 'Could not get a download link');
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  return (
    <>
      <button type="button" className="agent-download agent-download--button" onClick={open} disabled={loading}>
        {children}
      </button>
      {error && <span className="agent-inline-error" role="status">{error}</span>}
    </>
  );
};

export default DownloadButton;
